<template>
	<vue-modal
		:isShowModal="isExportModal"
		@close="$emit('close')"
		:style="'height: fit-content'"
	>
		<template #header>Eksporter journalnotater</template>
		<template #body>
			<template v-if="!isLoading">
				<div>
					<div class="form-body">
						<div class="row">
							<div
								ref="noteSectionRef"
								class="col-12"
								style="height: fit-content"
							>
								<div class="row">
									<div class="col-4 form-group">
										<department-select
											v-model="department"
											:terms="props.terms"
											:selectable="filteredDepartments"
										></department-select>
									</div>
									<div class="col-4 form-group">
										<employee-select
											v-model="employee"
										></employee-select>
									</div>
									<div class="col-4 form-group">
										<student-select
											v-model="student.selected"
											:studentId="
												parseInt(props.studentId)
											"
											:options="props.studentOptions"
											:isAll="
												!props.studentId ? true : false
											"
											:disabled="props.isStudentModule"
											@option:selected="studentSelected"
										>
										</student-select>
									</div>
								</div>
								<div class="row">
									<div class="col-4 form-group">
										<Select
											searchable
											v-model="category"
											:options="categoryOptions"
											:selectable="filteredCategories"
										/>
									</div>
									<div class="col-4 form-group">
										<Select
											searchable
											placeholder="Vælg plan"
											v-model="plan"
											:options="planOptions"
											:selectable="filteredPlans"
										/>
									</div>
									<div class="col-4 form-group">
										<Select
											searchable
											v-model="theme"
											:options="themeOptions"
											:selectable="filteredThemes"
										/>
									</div>
								</div>
								<div class="row">
									<div class="col-4 form-group">
										<Select
											searchable
											placeholder="Vælg type"
											v-model="journalType"
											:options="typeOptions"
										/>
									</div>
									<div class="col-4 form-group">
										<daterange-picker
											:isStartDateWeekBefore="true"
											@change="handleDate"
										></daterange-picker>
									</div>
									<div class="col-4 form-group">
										<div class="group">
											<input
												v-model="searchText"
												style="background: white"
												name="searchText"
												class="form-control"
												type="text"
											/>
											<span class="highlight"></span>
											<span class="bar"></span>
											<label>Søg på indhold</label>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div
							class="d-flex justify-content-end"
							style="column-gap: 1rem"
						>
							<button class="btn btn-primary" @click="exportToPdf">
								Eksporter til PDF
							</button>
						</div>
						<div
							class="separator form-group"
							style="margin-top: 15px"
						/>
					</div>
				</div>
				<div>
					<journal-card
						v-for="note in notes"
						:key="note.journal_id"
						:note="note"
						:isExport="true"
						style="margin-bottom: 15px"
						:terms="props.terms"
					/>
				</div>
			</template>
			<template v-else>
				<img
					class="loader"
					src="/safejournal/app-assets/images/common/loader.gif"
				/>
			</template>
		</template>
	</vue-modal>
</template>

<script setup>
import DaterangePicker from "@elements/datePicker/DaterangePicker.vue";
import vueModal from "@elements/form/vueModal.vue";
import MultiSelect from "@elements/select/BaseMultiSelect.vue";
import Select from "@elements/select/BaseSingleSelect.vue";
import DepartmentSelect from "@elements/select/single/DepartmentSelect.ce.vue";
import EmployeeSelect from "@elements/select/single/EmployeeSelect.vue";
import StudentSelect from "@elements/select/single/StudentSelect.vue";
import JournalService from "@services/JournalService.js";
import { endOfDay, startOfDay, subWeeks } from "date-fns";
import {
	computed,
	onBeforeMount,
	onMounted,
	ref,
	watch,
	watchEffect,
} from "vue";
import { useDebouncedRef } from "../../../helpers/debouncedRef";
import JournalCard from "../journalCard.vue";
import StudentService from "@services/StudentService";
const studentService = new StudentService();

const service = new JournalService();

const props = defineProps({
	isExportModal: Boolean,
	terms: Object,

	students: Array,
	department: Object,
	employee: Object,
	categoryOptions: Array,
	categories: Array,
	category: Object,
	themeOptions: Array,
	themes: Array,
	theme: Object,
	plan: Object,
	planOptions: Array,
	studentOptions: Array,
	employeeOptions: Array,
	departmentOptions: Array,
	studentId: Number,

	isStudentModule: Boolean,
	tab: String,
	types: Object,
	filters: Object,
});

const isLoading = ref(true);
const notes = ref([]);
const typeOptions = ref([]);
const journalType = ref(typeOptions.value[0]);
const department = ref(
	props.departmentOptions.find(
		(department) => department.option === props.department?.option,
	) || props.departmentOptions[0],
);
const filteredDepartments = (option) => {
	if (student.value.option !== "" && student.value.dep_ids.length > 0) {
		return (
			student.value.dep_ids.includes(option.option) ||
			option.option === ""
		);
	}
	return true;
};
onMounted(async () => {
	if (props.studentId) {
		const res = await studentService.get(props.studentId);
		student.value.selected = {
			label: `${res?.fname} ${res?.lname}`,
			option: res.student_id,
		};
		student.value.dep_ids = res.department_ids.split(",").map(Number);
	} else {
		student.value.selected = {
			label: `Alle ${props.terms.student.multi}`,
			option: "",
		};
	}
})

const employee = ref(
	props.employeeOptions.find(
		(employee) => employee.option === props.employee?.option,
	) || props.employeeOptions[0],
);
const student = ref({
	selected: {
		label: computed(() => `Alle ${props.terms.student.multi}`),
		option: "",
	},
	dep_ids: [],
});
const studentSelected = (selected) => {
	student.value.selected = selected;
};
watchEffect(() => {
	if (props.students.length > 0 && student.value.selected.option !== "") {
		student.value.dep_ids =
			props.students
				.find(
					(studentToFind) =>
						studentToFind.student_id === student.value.selected.option,
				)
				.department_ids.split(",")
				.map(Number) || [];
	} else {
		student.value.dep_ids = [];
	}
});
const category = ref(
	props.categoryOptions.find(
		(category) => category.option === props.filters.category,
	) ||
		props.categoryOptions.find(
			(category) => category.option === props.category?.option,
		) ||
		null,
);
const plan = ref(
	props.planOptions.find((plan) => plan.option === props.filters.plan) ||
		props.planOptions.find((plan) => plan.option === props.plan?.option) ||
		props.planOptions[0],
);
const filteredCategories = (option) => {
	const depIdsArray = option.dep_ids || [];

	const depIncluded =
		depIdsArray.some((depId) => student.value.dep_ids.includes(depId)) ||
		depIdsArray.some((depId) => employee.value.dep_ids.includes(depId)) ||
		depIdsArray.includes(department.value.option);

	const optionHasDeps = option.dep_ids && option.dep_ids.length > 0;

	return (
		(journalType.value.option === "all" ||
			option.type_id === journalType.value.option) &&
		(student.value.selected.option === "" ||
			!option.student_ids ||
			option.student_ids.length === 0 ||
			option.student_ids.includes(student.value.selected.option)) &&
		(!optionHasDeps ||
			(!employee.value?.option &&
				!student.value.selected.option &&
				!department.value.option) ||
			(optionHasDeps && depIncluded))
	);
};
const filteredPlans = (option) => {
	return (
		(journalType.value.option === "all" ||
			option.type_id === journalType.value.option) &&
		(student.value.selected.option === "" ||
			!option.student_id ||
			option.student_id === student.value.selected.option)
	);
};
const themeOptions = ref(props.themeOptions);
const placeholder = themeOptions.value.find((theme) => theme.option === 0)
if (placeholder) {
	placeholder.label = computed(() =>
		journalType.value.option === "all"
			? `Alle temaer/målepunkter`
			: `Alle ${
					journalType.value.option === 2 ? "målepunkter" : "temaer"
				}`,
	);
	placeholder.option = 0;
}
const theme = ref(
	themeOptions.value.find((theme) => theme.option === props.filters.theme) ||
		themeOptions.value.find(
			(theme) => theme.option === props.theme.option,
		) ||
		themeOptions.value[0],
);
watchEffect(() => {
	if (journalType.value?.option !== "all") {
		if (theme.value.type_id !== journalType.value?.option) {
			theme.value = themeOptions.value[0];
		}
	}
});
const filteredThemes = (option) => {
	const themeOption = props.themes.find(() => {
		return (
			journalType.value.option === "all" ||
			option.type_id === journalType.value.option ||
			option.option === 0
		);
	});
	return themeOption;
};

const getDepartment = computed(() => {
	if (Array.isArray(department.value)) {
		return department.value?.map((department) => department.option);
	} else {
		return department.value.option;
	}
});
const getEmployee = computed(() => {
	if (Array.isArray(employee.value)) {
		return employee.value?.map((employee) => employee.option);
	} else {
		return employee.value.option;
	}
});
const getStudent = computed(() => {
	if (Array.isArray(student.value.selected)) {
		return student.value.selected?.map((student) => student.option);
	} else {
		return student.value.selected?.option;
	}
});
const getCategory = computed(() => {
	return category.value.option === 0 ? null : category.value?.option;
});
const getTheme = computed(() => {
	return theme.value.option === 0 ? null : theme.value?.option;
});
const getPlan = computed(() => {
	return plan.value.option === 0 ? null : plan.value?.option;
});

const searchText = useDebouncedRef("");
const getType = computed(() => {
	return journalType.value?.option === "all"
		? null
		: journalType.value?.option;
});
const filters = ref({
	from: subWeeks(new Date(), 1),
	to: new Date(),
	searchText: searchText,
	employee: getEmployee,
	student: getStudent,
	category: getCategory,
	theme: getTheme,
	plan: getPlan,
	department: getDepartment,
	type: getType,
	page: 1,
});

watch(
	filters,
	async () => {
		fetchJournalNotes();
	},
	{ deep: true },
);
const fetchJournalNotes = async () => {
	try {
		notes.value = await service.getAll(filters.value)
			.then((response) => {
				let filteredResults = Object.values(response.data).filter(result => result.journal_id != null);
				if (journalType.value?.option !== 'all') filteredResults = filteredResults.filter(res => res.type_id == journalType.value?.option);
				return filteredResults;
			});
	} catch (error) {
		console.log(error.response?.data?.errors);
		console.log(error.response?.data?.message);
	} finally {
		isLoading.value = false;
	}
};
async function handleDate(selectedDate) {
	filters.value.from = startOfDay(selectedDate.value[0]);
	filters.value.to = endOfDay(selectedDate.value[1]);
}
const handleTypes = async () => {
	const allOption = {
		label: "Alle journaltyper",
		option: "all",
	};
	typeOptions.value.push(allOption);
	props.types.forEach((entry) => {
		const option = {
			label: entry.name,
			option: entry.id,
		};
		typeOptions.value.push(option);
	});
	if (props.tab) {
		let selectedType = typeOptions.value.find(
			(type) => type.option === props.tab,
		);
		journalType.value = selectedType;
	}
};

const exportToPdf = async () => {
	const ids = Object.values(notes.value).map((note) => note.journal_id);
	try {
		const response = await service.exportMulti(ids);

		const contentDisposition = response.headers["content-disposition"];
		const match = contentDisposition.match(/filename="(.+)"/);
		const fileName = match ? match[1] : "journal.pdf";

		const blob = new Blob([response.data], { type: "application/pdf" });
		const blobUrl = URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = blobUrl;
		link.download = fileName;
		link.click();

		setTimeout(() => {
			URL.revokeObjectURL(blobUrl);
		}, 100);
	} catch (error) {
		console.log(error);
	}
};
onBeforeMount(async () => {
	await handleTypes();
});
</script>
